import { useState, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";

/**
 * Tracks anonymous NPI searches against the backend /api/public/track-search
 * endpoint. Exposes:
 *   trackSearch()  → increments the counter, returns {reached_limit, remaining}
 *   status         → last known { count, limit, remaining, reached_limit }
 *   gateOpen       → boolean for showing the signup modal
 *   closeGate()    → dismiss the modal
 *
 * When the user is authenticated, trackSearch() is a no-op that returns
 * immediately — auth'd users have their own tier-based rate limiting.
 */
export default function useAnonymousSearchTracker() {
  const { isAuthenticated, loading } = useAuth();
  const [status, setStatus] = useState({ count: 0, limit: 3, remaining: 3, reached_limit: false });
  const [gateOpen, setGateOpen] = useState(false);

  // On mount, fetch current status so we gate BEFORE even the 4th search attempt.
  useEffect(() => {
    if (loading || isAuthenticated) return;
    fetch(`${window.location.origin}/api/public/search-status`, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setStatus(d); })
      .catch(() => { /* non-fatal */ });
  }, [isAuthenticated, loading]);

  const trackSearch = useCallback(async () => {
    if (isAuthenticated) return { reached_limit: false, remaining: Infinity };
    try {
      const res = await fetch(`${window.location.origin}/api/public/track-search`, {
        method: "POST",
        credentials: "omit",
      });
      if (!res.ok) return { reached_limit: false, remaining: 0 };
      const d = await res.json();
      setStatus(d);
      if (d.reached_limit) setGateOpen(true);
      return d;
    } catch {
      return { reached_limit: false, remaining: 0 };
    }
  }, [isAuthenticated]);

  const closeGate = useCallback(() => setGateOpen(false), []);
  const openGate = useCallback(() => setGateOpen(true), []);

  return { status, trackSearch, gateOpen, openGate, closeGate };
}
